<template>
  <div class="title">
    Select a Folder
  </div>
  <div class="body">
    <div
      v-if="loading"
      class="loading-wrapper"
    >
      <i class="fal fa-cog fa-spin sh-gear" />
    </div>
    <div
      v-else
      class="select-wrapper"
    >
      <select
        ref="targetFolder"
        v-model="selectedFolder"
        style="width: 100%;"
      >
        <option value="">
          -- Select Folder --
        </option>
        <option
          v-for="folder in nestedFoldersWithOptions"
          :key="folder.id"
          :value="folder"
        >
          {{ folder.title }}
        </option>
      </select>
    </div>
    <div class="modal-buttons">
      <button
        class="secondary"
        @click="close"
      >
        Cancel
      </button>
      <button
        class="primary"
        :disabled="!selectedFolder"
        @click="save"
      >
        Save
      </button>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters, mapState } from "vuex"

  export default {
    props: {
      contextShare: {
        required: true,
        type: Number,
      },
      channelId: {
        required: true,
        type: Number,
      },
      rallyId: {
        required: true,
        type: Number,
      },
    },
    data() {
      return {
        loading: true,
        selectedFolder: null,
      };
    },
    computed: {
      ...mapGetters("contents", ["folderStructureByChannel"]),
      ...mapState("rallies", ["allRalliesByRallyId"]),
      // Get cached folder structure from store
      folderList() {
        return this.folderStructureByChannel(this.channelId) || [];
      },
      nestedFolders() {
        const foldersById = {};
        const topLevelFolders = [];
        const clonedFolders = this.folderList.map(folder => ({
          ...folder,
          children: [],
          attributes: {
            ...folder.attributes,
            'nest-level': folder.attributes['nest-level'] || 1
          }
        }));

        for (const folder of clonedFolders) {
          foldersById[folder.id] = folder;
        }

        for (const folder of clonedFolders) {
          if (folder.parentId && foldersById[folder.parentId]) {
            const parent = foldersById[folder.parentId];
            folder.attributes['nest-level'] = parent.attributes['nest-level'] + 1;
            parent.children.push(folder);
          } else {
            topLevelFolders.push(folder);
          }
        }

        // Add synthetic root
        topLevelFolders.unshift({
          id: this.channelId,
          children: [],
          attributes: {
            editable: true,
            type: 'RootFolder',
            title: '** HOME **'
          }
        });

        return topLevelFolders;
      },
      nestedFoldersWithOptions() {
        return this.flattenNestedFolders(this.nestedFolders);
      }
    },
    async beforeMount() {
      this.clearFolderStructureCache({ channelId: this.channelId })
      const loadingComplete = await this.fetchFolderStructureForChannel()
      this.loading = !loadingComplete
    },
    methods: {
      ...mapActions("contents", ["clearFolderStructureCache", "fetchFoldersRecursive", "moveContent"]),
      ...mapActions("user", ["setModalProps"]),
      close() {
        this.setModalProps({})
      },
      async fetchFolderStructureForChannel() {
        try {
          const confirmation = await this.fetchFoldersRecursive({
            contextRallyId: this.rallyId,
            contextChannelId: this.channelId,
            contextId: this.channelId,
            contextType: 'Channel',
            nestLevel: 1,
            result: []
          })
          return confirmation
        } catch (error) {
          console.error('Error fetching folder structure:', error)
        }
      },
      flattenNestedFolders(folders, level = 0) {
        let result = [];

        for (const folder of folders) {
          // Check if folder is editable and is a Folder type (or RootFolder)
          if (folder.attributes && folder.attributes.editable &&
              (folder.attributes.type === "Folder" || folder.attributes.type === "RootFolder")) {

            // Create the display title with proper indentation
            let displayTitle = folder.attributes.title;
            if (folder.attributes.type !== "RootFolder" && level > 0) {
              // Add dashes for non-root folders based on their level
              const prefix = '-'.repeat(level) + ' ';
              displayTitle = prefix + displayTitle;
            }

            result.push({
              id: folder.id,
              title: displayTitle,
              type: folder.attributes.type,
              originalTitle: folder.attributes.title // Keep original title for reference
            });

            // Recursively process children
            if (folder.children && folder.children.length > 0) {
              const childResults = this.flattenNestedFolders(folder.children, level + 1);
              result = result.concat(childResults);
            }
          }
        }

        return result;
      },
      async move(params) {
        // Handle the logic for moving the folder
        await this.moveContent({
          shareId: this.contextShare.id,
          destinationId: params.destinationFolderId,
          destinationType: params.contextType
        })
        this.$store.dispatch('user/flashSuccess', 'File successfully moved!');
        this.close()
        window.location.reload();
      },
      save() {
        if (!this.selectedFolder) {
          return;
        }

        const destinationFolderId = this.selectedFolder.id;
        let contextType = 'Container';

        if (this.selectedFolder.type === "RootFolder") {
          contextType = 'Channel';
        }

        this.move({ destinationFolderId, contextType });
        this.selectedFolder = null;
      }
    },
  }
</script>

<style>
  .loading-wrapper {
    color: #008592;
    font-size: 2rem;
    margin-bottom: 35px;
    text-align: center;
  }

  .select-wrapper {
    background: #FFFFFF;
    border: 1px solid #bdbdbd;
    border-radius: 5px;
    cursor: pointer;
    display: inline-block;
    font-size: 1.2rem;
    padding: 10px 16px;
    position: relative;
    margin-bottom: 20px;
    width: 100%;
  }
  .select-wrapper select {
    background: #FFFFFF;
    border: none;
  }

  .move-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9999;
  }

  .move-modal-content {
    background-color: white;
    padding: 20px;
    border-radius: 5px;
    text-align: center;
    max-width: 400px;
    width: 90%;
  }

  .move-modal-content h2 {
    margin-top: 0;
    margin-bottom: 20px;
  }

  .move-modal-content select {
    width: 100%;
    padding: 8px;
    margin-bottom: 20px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
  }

  .modal-buttons button {
    margin-left: initial;
  }
</style>
